import { stateLabel, normalizeState } from "@/utils/usStates.js";

export { stateLabel };

function normalizeCity(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\./g, "")
    .replace(/\s+/g, " ");
}

function normalizeCounty(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\./g, "")
    .replace(/\s+(county|parish|borough)$/i, "")
    .replace(/\s+/g, " ");
}

function zip5(value) {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.slice(0, 5);
}

function joinList(items, conjunction = "or") {
  const list = items.filter(Boolean);
  if (list.length === 0) return "";
  if (list.length === 1) return list[0];
  if (list.length === 2) return `${list[0]} ${conjunction} ${list[1]}`;
  return `${list.slice(0, -1).join(", ")}, ${conjunction} ${list[list.length - 1]}`;
}

function pointInRing(lng, lat, ring) {
  if (!Array.isArray(ring) || ring.length < 3) return false;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = Number(ring[i]?.[0]);
    const yi = Number(ring[i]?.[1]);
    const xj = Number(ring[j]?.[0]);
    const yj = Number(ring[j]?.[1]);
    if (![xi, yi, xj, yj].every(Number.isFinite)) continue;
    const intersects = (yi > lat) !== (yj > lat)
      && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

function polygonGeometry(geojson) {
  if (!geojson || typeof geojson !== "object") return null;
  if (geojson.type === "Feature") return polygonGeometry(geojson.geometry);
  if (geojson.type === "FeatureCollection") return polygonGeometry(geojson.features?.[0]);
  if (geojson.type === "Polygon" || geojson.type === "MultiPolygon") return geojson;
  return null;
}

function pointInGeometry(lng, lat, geojson) {
  const geometry = polygonGeometry(geojson);
  if (!geometry) return null;
  const polygons = geometry.type === "Polygon"
    ? [geometry.coordinates]
    : geometry.coordinates;
  if (!Array.isArray(polygons)) return null;
  return polygons.some((poly) => {
    if (!Array.isArray(poly) || !poly.length) return false;
    if (!pointInRing(lng, lat, poly[0])) return false;
    for (let hole = 1; hole < poly.length; hole += 1) {
      if (pointInRing(lng, lat, poly[hole])) return false;
    }
    return true;
  });
}

function geographyStatus(match, place) {
  const type = match?.type;
  if (!place) {
    return { status: "unknown", reason: "There is no address to compare." };
  }
  if (type === "platform") {
    return { status: "match", reason: "This rule applies everywhere." };
  }
  if (type === "state") {
    const wanted = normalizeState(match.state);
    const actual = normalizeState(place.state);
    if (!wanted) return { status: "unknown", reason: "This rule has no state saved." };
    if (!actual) return { status: "unknown", reason: "The address has no state." };
    if (actual === wanted) {
      return { status: "match", reason: `The address is in ${stateLabel(wanted)}.` };
    }
    return {
      status: "miss",
      reason: `The address is in ${stateLabel(actual)}, and this rule only covers ${stateLabel(wanted)}.`,
    };
  }
  if (type === "state_city") {
    const wantedState = normalizeState(match.state);
    const actualState = normalizeState(place.state);
    const wantedCity = normalizeCity(match.city);
    const actualCity = normalizeCity(place.city);
    if (!wantedState || !wantedCity) {
      return { status: "unknown", reason: "This rule is missing a state or city." };
    }
    if (!actualState || !actualCity) {
      return { status: "unknown", reason: "The address is missing a state or city." };
    }
    if (actualState === wantedState && actualCity === wantedCity) {
      return { status: "match", reason: `The address is in ${match.city}, ${stateLabel(wantedState)}.` };
    }
    return {
      status: "miss",
      reason: `The address is in ${place.city || "an unknown city"}, ${stateLabel(actualState) || "an unknown state"}. This rule covers ${match.city}, ${stateLabel(wantedState)}.`,
    };
  }
  if (type === "state_county") {
    const wantedState = normalizeState(match.state);
    const actualState = normalizeState(place.state);
    const wantedCounty = normalizeCounty(match.county);
    const actualCounty = normalizeCounty(place.county);
    if (!wantedState || !wantedCounty) {
      return { status: "unknown", reason: "This rule is missing a state or county." };
    }
    if (!actualState) return { status: "unknown", reason: "The address has no state." };
    if (!actualCounty) {
      return { status: "unknown", reason: "The address has no county, so this county rule can't be decided." };
    }
    if (actualState === wantedState && actualCounty === wantedCounty) {
      return {
        status: "match",
        reason: `The address is in ${match.county} County, ${stateLabel(wantedState)}.`,
      };
    }
    return {
      status: "miss",
      reason: `The address is in ${place.county || "another"} County, ${stateLabel(actualState)}. This rule covers ${match.county} County, ${stateLabel(wantedState)}.`,
    };
  }
  if (type === "zip_codes") {
    const zips = (Array.isArray(match.zip_codes) ? match.zip_codes : []).map(zip5).filter(Boolean);
    const actual = zip5(place.zip);
    if (!zips.length) return { status: "unknown", reason: "This rule has no ZIP codes saved." };
    if (!actual) return { status: "unknown", reason: "The address has no ZIP code." };
    if (zips.includes(actual)) {
      return { status: "match", reason: `The address is in ZIP ${actual}.` };
    }
    return { status: "miss", reason: `ZIP ${actual} is not one of the ${zips.length} ZIP codes on this rule.` };
  }
  if (type === "geojson") {
    if (place.lat == null || place.lng == null) {
      return { status: "unknown", reason: "The address has no map pin, so the custom area can't be checked." };
    }
    const inside = pointInGeometry(place.lng, place.lat, match.geojson);
    if (inside == null) {
      return { status: "unknown", reason: "This rule's map area couldn't be read." };
    }
    if (inside) return { status: "match", reason: "The address falls inside this rule's custom map area." };
    return { status: "miss", reason: "The address is outside this rule's custom map area." };
  }
  return { status: "unknown", reason: "This rule's area type isn't recognized." };
}

function locationStatus(rule, storePlace, deliveryPlace) {
  const addressType = rule.address_type || "delivery";
  if (addressType === "platform" || rule.match?.type === "platform") {
    return { status: "match", reason: "This rule applies everywhere." };
  }

  if (addressType === "store") {
    if (!storePlace) {
      return {
        status: "unknown",
        reason: "This rule looks at the store's address. Choose a store, or look one up from an address.",
      };
    }
    const geo = geographyStatus(rule.match, storePlace);
    if (geo.status === "match") return { status: "match", reason: `Store address: ${geo.reason}` };
    if (geo.status === "miss") return { status: "miss", reason: `Store address: ${geo.reason}` };
    return { status: "unknown", reason: `Store address: ${geo.reason}` };
  }

  if (addressType === "delivery") {
    if (!deliveryPlace) {
      return {
        status: "unknown",
        reason: "This rule looks at the customer's delivery address. A store address does not tell us where the order is dropped off.",
      };
    }
    const geo = geographyStatus(rule.match, deliveryPlace);
    if (geo.status === "match") return { status: "match", reason: `Delivery address: ${geo.reason}` };
    if (geo.status === "miss") return { status: "miss", reason: `Delivery address: ${geo.reason}` };
    return { status: "unknown", reason: `Delivery address: ${geo.reason}` };
  }

  if (!storePlace && !deliveryPlace) {
    return {
      status: "unknown",
      reason: "This rule matches if either the store or the delivery address is in the area. Neither address is available.",
    };
  }

  const storeGeo = storePlace ? geographyStatus(rule.match, storePlace) : null;
  const deliveryGeo = deliveryPlace ? geographyStatus(rule.match, deliveryPlace) : null;
  if (storeGeo?.status === "match") {
    return { status: "match", reason: `Store address: ${storeGeo.reason}` };
  }
  if (deliveryGeo?.status === "match") {
    return { status: "match", reason: `Delivery address: ${deliveryGeo.reason}` };
  }
  const unknown = [storeGeo, deliveryGeo].find((geo) => geo?.status === "unknown");
  if (unknown) return { status: "unknown", reason: unknown.reason };
  if (!deliveryPlace) {
    return {
      status: "unknown",
      reason: `Store address: ${storeGeo?.reason || "The store is outside this area."} This rule can also match a delivery address, which we don't have.`,
    };
  }
  if (!storePlace) {
    return {
      status: "unknown",
      reason: "The delivery address is outside this area, but the store address wasn't checked.",
    };
  }
  return { status: "miss", reason: "Neither the store address nor the delivery address is in this rule's area." };
}

function namesFor(ids, lookup) {
  return ids.map((id) => lookup.get(id) || id);
}

function productTarget(product, rule, categoryNameById, tagLabelById) {
  const target = rule.target || {};
  const type = target.type || "categories";
  const categoryIds = (Array.isArray(target.category_ids) ? target.category_ids : []).map(String);
  const exceptIds = (Array.isArray(target.except_category_ids) ? target.except_category_ids : []).map(String);
  const ruleTags = (Array.isArray(target.tags) ? target.tags : []).map(String);

  const matchedCategories = categoryIds.filter((id) => product.categoryIds.has(id));
  const inExcept = exceptIds.some((id) => product.categoryIds.has(id));
  const requiresCategories = type === "categories"
    || type === "categories_tags_any"
    || type === "categories_tags_all";
  const categoryScope = requiresCategories
    ? matchedCategories.length > 0
    : categoryIds.length === 0 || matchedCategories.length > 0;
  const hasAny = ruleTags.some((id) => product.tagIds.has(id));
  const hasAll = ruleTags.length > 0 && ruleTags.every((id) => product.tagIds.has(id));
  const catText = joinList(namesFor(matchedCategories.length ? matchedCategories : categoryIds, categoryNameById));
  const tagText = joinList(namesFor(ruleTags, tagLabelById));
  const scopeText = categoryIds.length
    ? ` in ${catText || "the selected categories"}`
    : "";

  let match = false;
  let because = "This product is not covered by the rule.";

  if (type === "categories") {
    match = matchedCategories.length > 0;
    because = match
      ? `It is in ${joinList(namesFor(matchedCategories, categoryNameById))}.`
      : "It is not in the categories on this rule.";
  } else if (type === "tags_any" || type === "categories_tags_any") {
    const needsCategory = type === "categories_tags_any" || categoryIds.length > 0;
    match = hasAny && (!needsCategory || categoryScope);
    if (match) {
      const held = ruleTags.filter((id) => product.tagIds.has(id));
      because = `It has ${joinList(namesFor(held, tagLabelById))}${scopeText}.`;
    } else if (needsCategory && !categoryScope) {
      because = "It is outside the categories on this rule.";
    } else {
      because = `It does not have ${tagText || "the required tag"}.`;
    }
  } else if (type === "tags_all" || type === "categories_tags_all") {
    const needsCategory = type === "categories_tags_all" || categoryIds.length > 0;
    match = hasAll && (!needsCategory || categoryScope);
    if (match) {
      because = `It has all of ${tagText}${scopeText}.`;
    } else if (needsCategory && !categoryScope) {
      because = "It is outside the categories on this rule.";
    } else {
      const missing = ruleTags.filter((id) => !product.tagIds.has(id));
      because = `It is missing ${joinList(namesFor(missing, tagLabelById), "and") || "a required tag"}.`;
    }
  } else if (type === "permissive_tags_any") {
    match = categoryScope && !inExcept && ruleTags.length > 0 && !hasAny;
    if (inExcept) because = "It is in an exception category, so this rule leaves it available.";
    else if (!categoryScope) because = "It is outside the categories on this rule.";
    else if (match) because = `It has none of ${tagText}${scopeText}.`;
    else because = `It has ${tagText}, so the requirement is met.`;
  } else if (type === "permissive_tags_all") {
    match = categoryScope && !inExcept && ruleTags.length > 0 && !hasAll;
    if (inExcept) because = "It is in an exception category, so this rule leaves it available.";
    else if (!categoryScope) because = "It is outside the categories on this rule.";
    else if (match) {
      const missing = ruleTags.filter((id) => !product.tagIds.has(id));
      because = `It is missing ${joinList(namesFor(missing, tagLabelById), "and")}${scopeText}.`;
    } else {
      because = `It has all of ${tagText}.`;
    }
  }

  return { match, because };
}

export function ruleName(rule) {
  return String(rule?.name || "").trim() || "Untitled rule";
}

export function restrictAction(restrict) {
  if (restrict === "pickup") return "Blocks pickup";
  if (restrict === "delivery") return "Blocks delivery";
  return "Blocks pickup and delivery";
}

function restricts(rule, channel) {
  return rule.restrict === "both" || rule.restrict === channel || !rule.restrict;
}

function channelResult(channel, applied, unchecked) {
  const blocking = applied.filter((item) => restricts(item.rule, channel));
  const maybe = unchecked.filter((item) => restricts(item.rule, channel));
  if (blocking.length) {
    const title = ruleName(blocking[0].rule);
    return {
      status: "blocked",
      summary: blocking.length === 1
        ? `Blocked by “${title}”.`
        : `Blocked by ${blocking.length} rules, including “${title}”.`,
    };
  }
  if (maybe.length) {
    return {
      status: "unknown",
      summary: maybe.length === 1
        ? `Not blocked yet. “${ruleName(maybe[0].rule)}” still needs location detail.`
        : `Not blocked yet. ${maybe.length} rules still need location detail.`,
    };
  }
  return {
    status: "allowed",
    summary: channel === "pickup" ? "No rule blocks pickup." : "No rule blocks delivery.",
  };
}

function saleFrom(pickup, delivery, targeted) {
  if (!targeted) {
    return {
      status: "yes",
      title: "YES",
      detail: "No georestriction rule targets this product, so pickup and delivery are both allowed.",
    };
  }
  if (pickup.status === "blocked" && delivery.status === "blocked") {
    return {
      status: "no",
      title: "NO",
      detail: "Pickup and delivery are both blocked.",
    };
  }
  if (pickup.status === "allowed" && delivery.status === "allowed") {
    return {
      status: "yes",
      title: "Can be sold",
      detail: "Pickup and delivery are both allowed.",
    };
  }
  if (pickup.status === "allowed" && delivery.status === "blocked") {
    return {
      status: "yes",
      title: "YES, but pckup only",
      detail: "Delivery is blocked. Customers can still pick this up.",
    };
  }
  if (pickup.status === "blocked" && delivery.status === "allowed") {
    return {
      status: "yes",
      title: "YES, but delivery only",
      detail: "Pickup is blocked. Customers can still have this delivered.",
    };
  }
  if (pickup.status === "allowed" && delivery.status === "unknown") {
    return {
      status: "yes",
      title: "YES for pickup, unknown for delivery",
      detail: "Delivery still depends on a rule we couldn't fully check.",
    };
  }
  if (pickup.status === "blocked" && delivery.status === "unknown") {
    return {
      status: "unknown",
      title: "NO for pickup, unknown for delivery",
      detail: "Delivery might still be possible. Check the rules that need more location detail.",
    };
  }
  if (pickup.status === "unknown" && delivery.status === "allowed") {
    return {
      status: "yes",
      title: "YES for delivery, unknown for pickup",
      detail: "Pickup still depends on a rule we couldn't fully check.",
    };
  }
  if (pickup.status === "unknown" && delivery.status === "blocked") {
    return {
      status: "unknown",
      title: "NO for delivery, unknown for pickup",
      detail: "Pickup might still be possible. Check the rules that need more location detail.",
    };
  }
  return {
    status: "unknown",
    title: "Not enough location detail",
    detail: "Rules target this product, but we need a store or address before we can say if it can be sold.",
  };
}

export function evaluateCompliance({
  product,
  rules,
  storePlace = null,
  deliveryPlace = null,
  categories = [],
  tagOptions = [],
}) {
  const categoryNameById = new Map(categories.map((c) => [String(c.id), c.name]));
  const tagLabelById = new Map(
    tagOptions.map((tag) => [String(tag.id), tag.display?.value || tag.id])
  );
  const applied = [];
  const unchecked = [];
  const notHere = [];
  const inactive = [];

  for (const rule of rules || []) {
    const target = productTarget(product, rule, categoryNameById, tagLabelById);
    if (!target.match) continue;
    const where = locationStatus(rule, storePlace, deliveryPlace);
    const item = {
      rule,
      because: target.because,
      where: where.reason,
      action: restrictAction(rule.restrict),
    };
    if (rule.active === false) {
      if (where.status === "match") inactive.push(item);
      continue;
    }
    if (where.status === "match") applied.push(item);
    else if (where.status === "unknown") unchecked.push(item);
    else notHere.push(item);
  }

  const pickup = channelResult("pickup", applied, unchecked);
  const delivery = channelResult("delivery", applied, unchecked);
  const targeted = applied.length + unchecked.length + notHere.length + inactive.length > 0;

  return {
    sale: saleFrom(pickup, delivery, targeted),
    pickup,
    delivery,
    applied,
    unchecked,
    notHere,
    inactive,
  };
}

function normAddress(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/\./g, "")
    .replace(/\bstreet\b/g, "st")
    .replace(/\bavenue\b/g, "ave")
    .replace(/\broad\b/g, "rd")
    .replace(/\bdrive\b/g, "dr")
    .replace(/\bboulevard\b/g, "blvd")
    .replace(/\bnorth\b/g, "n")
    .replace(/\bsouth\b/g, "s")
    .replace(/\beast\b/g, "e")
    .replace(/\bwest\b/g, "w")
    .replace(/[^a-z0-9]/g, "");
}

function metersBetween(a, b) {
  if (a?.lat == null || a?.lng == null || b?.lat == null || b?.lng == null) return null;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371000 * Math.asin(Math.min(1, Math.sqrt(h)));
}

function storePolygon(store) {
  let raw = store?.polygon || store?.delivery_polygon;
  if (!raw) return null;
  if (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (raw?.type === "Polygon" || raw?.type === "MultiPolygon" || raw?.type === "Feature" || raw?.type === "FeatureCollection") {
    return raw;
  }
  if (Array.isArray(raw?.coordinates)) {
    return { type: "Polygon", coordinates: raw.coordinates };
  }
  return null;
}

export function storesCoveringAddress(stores, place) {
  if (!place || place.lat == null || place.lng == null || !stores?.length) return [];
  return stores
    .filter((store) => {
      const polygon = storePolygon(store);
      if (!polygon) return false;
      return pointInGeometry(place.lng, place.lat, polygon) === true;
    })
    .sort((a, b) => String(a.name || a.store_id).localeCompare(String(b.name || b.store_id)));
}

export function findStoreForAddress(stores, place) {
  if (!place || !stores?.length) return null;
  const zip = zip5(place.zip);
  const street = normAddress(place.address);
  const ranked = [];

  for (const store of stores) {
    const saved = placeFromStore(store);
    const savedStreet = normAddress(saved.address || store.address);
    const savedZip = zip5(saved.zip || store.zip);
    let score = 0;
    let method = "";

    if (street && savedStreet && street === savedStreet && zip && savedZip === zip) {
      score = 100;
      method = "Same street address and ZIP.";
    } else if (street && savedStreet && zip && savedZip === zip && (savedStreet.includes(street) || street.includes(savedStreet))) {
      score = 80;
      method = "Street and ZIP are a close match.";
    }

    const meters = metersBetween(place, saved);
    if (meters != null && meters <= 800) {
      const nearScore = 70 - meters / 40;
      if (nearScore > score) {
        score = nearScore;
        method = `About ${Math.max(1, Math.round(meters))}m from this address.`;
      }
    }

    if (score > 0) ranked.push({ store, score, meters: meters ?? Number.POSITIVE_INFINITY, method });
  }

  ranked.sort((a, b) => b.score - a.score || a.meters - b.meters);
  const best = ranked[0];
  if (!best) return null;
  return { store: best.store, method: best.method };
}

export function placeFromStore(store) {
  if (!store) return null;
  const coords = store.location?.coordinates || store.coordinates;
  let lng = null;
  let lat = null;
  if (Array.isArray(coords) && coords.length >= 2) {
    lng = Number(coords[0]);
    lat = Number(coords[1]);
  }
  if (store.lat != null && store.lat !== "") lat = Number(store.lat);
  if (store.lng != null && store.lng !== "") lng = Number(store.lng);
  return {
    address: store.address || "",
    city: store.city || "",
    state: store.state || "",
    county: String(store.county || "").replace(/\s+(county|parish|borough)$/i, "").trim(),
    zip: store.zip || store.postal_code || "",
    lat: Number.isFinite(lat) ? lat : null,
    lng: Number.isFinite(lng) ? lng : null,
    formatted: [store.address, [store.city, store.state].filter(Boolean).join(", "), store.zip]
      .filter(Boolean)
      .join(" "),
  };
}

export function parseGooglePlace(place) {
  if (!place?.address_components) return null;
  const component = (type, name = "short_name") =>
    place.address_components.find((part) => part.types?.includes(type))?.[name] || "";
  const number = component("street_number");
  const street = component("route", "long_name");
  const city = component("locality", "long_name")
    || component("postal_town", "long_name")
    || component("sublocality", "long_name")
    || component("neighborhood", "long_name");
  const state = component("administrative_area_level_1", "short_name");
  const countyRaw = component("administrative_area_level_2", "long_name");
  const latFn = place.geometry?.location?.lat;
  const lngFn = place.geometry?.location?.lng;
  const lat = typeof latFn === "function" ? latFn.call(place.geometry.location) : null;
  const lng = typeof lngFn === "function" ? lngFn.call(place.geometry.location) : null;
  return {
    address: [number, street].filter(Boolean).join(" "),
    formatted: place.formatted_address || "",
    city,
    state,
    county: countyRaw.replace(/\s+(county|parish|borough)$/i, "").trim(),
    zip: component("postal_code"),
    lat: Number.isFinite(lat) ? lat : null,
    lng: Number.isFinite(lng) ? lng : null,
  };
}

export function mergePlaces(primary, fallback) {
  if (!primary) return fallback || null;
  if (!fallback) return primary;
  const next = { ...fallback, ...primary };
  for (const key of ["address", "city", "state", "county", "zip", "formatted"]) {
    if (!String(primary[key] || "").trim() && fallback[key]) next[key] = fallback[key];
  }
  if (primary.lat == null && fallback.lat != null) next.lat = fallback.lat;
  if (primary.lng == null && fallback.lng != null) next.lng = fallback.lng;
  return next;
}

function addCategoryRef(ref, categories, ids, names) {
  const values = [];
  if (ref == null) return;
  if (typeof ref === "string" || typeof ref === "number") values.push(String(ref));
  else if (typeof ref === "object") {
    ["id", "_id", "name", "label", "tag"].forEach((key) => {
      if (ref[key] != null && ref[key] !== "") values.push(String(ref[key]));
    });
  }
  values.forEach((value) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    const needle = trimmed.toLowerCase();
    const match = categories.find((c) => c.id === trimmed)
      || categories.find((c) => c.name.toLowerCase() === needle)
      || categories.find((c) => c.tag && c.tag.toLowerCase() === needle);
    if (!match) return;
    ids.add(match.id);
    if (!names.includes(match.name)) names.push(match.name);
  });
}

function addTagRef(ref, tagOptions, ids, labels) {
  const raw = typeof ref === "object" && ref
    ? (ref.id || ref.value || ref.display?.value || "")
    : ref;
  const trimmed = String(raw || "").trim();
  if (!trimmed) return;
  const match = tagOptions.find((tag) => tag.id === trimmed)
    || tagOptions.find((tag) => (tag.display?.value || "").toLowerCase() === trimmed.toLowerCase());
  const id = match?.id || trimmed;
  ids.add(id);
  const label = match?.display?.value || id;
  if (!labels.includes(label)) labels.push(label);
}

function collectRefs(source, key) {
  if (!source) return [];
  const value = source[key];
  if (Array.isArray(value)) return value;
  if (value == null || value === "") return [];
  return [value];
}

export function buildProductProfile({ hit, details, categories, tagOptions }) {
  const categoryIds = new Set();
  const categoryNames = [];
  const tagIds = new Set();
  const tagLabels = [];
  const sources = [hit, details].filter(Boolean);
  sources.forEach((source) => {
    ["default_category", "base_category", "category"].forEach((key) => {
      collectRefs(source, key).forEach((ref) => addCategoryRef(ref, categories, categoryIds, categoryNames));
    });
    ["categories", "category_ids"].forEach((key) => {
      collectRefs(source, key).forEach((ref) => addCategoryRef(ref, categories, categoryIds, categoryNames));
    });
    collectRefs(source, "attribute_tags").forEach((ref) => addTagRef(ref, tagOptions, tagIds, tagLabels));
  });

  const doc = details || hit || {};
  const title = doc.title || hit?.title || "";
  const subtitle = doc.subtitle || hit?.subtitle || "";
  return {
    sku: doc.sku_id || doc.sku || hit?.sku_id || "",
    title,
    subtitle,
    company: doc.company || hit?.company || "",
    image: doc.image || doc.image_large || hit?.image || "",
    upc: doc.upc_code || hit?.upc_code || "",
    volume: doc.volume_label || doc.volume || hit?.volume_label || hit?.volume || "",
    disabled: Boolean(doc.disabled ?? hit?.disabled),
    categoryIds,
    categoryNames,
    tagIds,
    tagLabels,
  };
}

export function blockedReasonAtStore({ hit, rules, categories, tagOptions, store }) {
  if (!hit || !rules?.length) return null;
  const profile = buildProductProfile({
    hit,
    details: null,
    categories: categories || [],
    tagOptions: tagOptions || [],
  });
  const result = evaluateCompliance({
    product: profile,
    rules,
    storePlace: placeFromStore(store),
    deliveryPlace: null,
    categories: categories || [],
    tagOptions: tagOptions || [],
  });
  if (result?.sale?.status !== "no") return null;
  const names = [];
  for (const item of result.applied || []) {
    const name = ruleName(item.rule);
    if (name && !names.includes(name)) names.push(name);
  }
  return names.length
    ? `Blocked at this store by ${names.join(", ")}.`
    : "Blocked at this store by a georestriction rule.";
}
